/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/* eslint-disable sonarjs/no-hardcoded-passwords -- locale key names are identifiers, not credentials */

export const LocaleKeys = {
  // Common Locale Keys
  ACTION_CANCEL: "common_cancel",
  ALERT_ACTION_CLOSE: "common_close",
  ACTION_DISMISS: "common_dismiss",
  ALERT_ACTION_OK: "common_ok",
  HOME_TILE_PREVIEW_TODAY: "common_today",
  DESCRIPTION_NOT_AVAILABLE: "common_description_not_available",
  FIRST_AIRED: "common_first_aired",
  FIRST_AIRS: "common_first_airs",
  TITLE_NOT_AVAILABLE: "common_title_not_available",
  NEED_HELP_MESSAGE: "common_need_help_message",
  NEED_HELP_TEXT: "common_need_help",
  ACTION_TRY_AGAIN: "common_try_again",
  ACTION_BACK: "common_back",
  /** "Retry" — manual retry button on the sign-in connection-error overlay. */
  ACTION_RETRY: "common_retry",
  /** "Exit" — closes the app from the sign-in connection-error overlay (TV only). */
  ACTION_EXIT: "app_exit_dialog_exit_button_text",
  /**
   * Support handle shown under a sign-in error, e.g. "Error code: A431".
   * The code is what a user reads to a support agent, so it is rendered
   * rather than only logged (UAW-2416 AC-2).
   */
  SIGN_IN_ERROR_CODE_LABEL: "sign_in_error_code_label",
  SIGN_IN_ACTION_SIGN_IN_AGAIN: "sign_in_action_sign_in_again",
  /**
   * Generic "still there" inactivity OSD message reused across all
   * non-playback screens (e.g. Search). Playback screens use the more
   * specific `LIVE_TV_INACTIVITY_OSD_MESSAGE` key instead.
   */
  COMMON_INACTIVITY_OSD_MESSAGE: "common_inactivity_osd_message",
  /** Generic failure toast for any request that cannot be processed. */
  REQUEST_FAILED_TOAST: "request_failed_toast",

  /**
   * Fallback body when the global-endpoint (TSRI) lookup fails at startup.
   * Nothing can be reached until it succeeds, so the copy points at the
   * connection rather than at a feature.
   */
  APP_STARTUP_ENDPOINTS_ERROR_MESSAGE: "app_startup_endpoints_error_message",

  // Auth Configuration related Locale Keys
  AUTH_CONFIG_DEMARSHALLED_ERROR_MESSAGE:
    "auth_config_demarshalled_error_message",
  AUTH_CONFIG_PARTIAL_RESPONSE_MESSAGE: "auth_config_partial_response_message",
  AUTH_CONFIG_NO_DOMAIN_MESSAGE: "auth_config_no_domain_message",
  AUTH_CONFIG_NO_LICENSE_PLATE_URL_MESSAGE:
    "auth_config_no_license_plate_url_message",
  AUTH_CONFIG_NO_AUTHORIZATION_SCHEME_MESSAGE:
    "auth_config_no_authorization_scheme_message",

  // Device Provisioning related Locale Keys
  DEVICE_PROVISIONING_ERROR_MESSAGE: "device_provisioning_error_message",

  // Feature Provisioning related Locale Keys
  FE_PROVISIONING_DEMARSHAL_ERROR_MESSAGE:
    "fe_provisioning_demarshal_error_message",
  FE_PROVISIONING_PARTIAL_RESPONSE_MESSAGE:
    "fe_provisioning_partial_response_message",
  FE_PROVISIONING_SERVICE_REQUEST_FAILED:
    "fe_provisioning_service_request_failed",

  // See More (grid page reached from a carousel's "See More" tile)
  SEE_MORE_TILE_LABEL: "see_more_tile_label",
  SEE_MORE_EMPTY_MESSAGE: "see_more_empty_message",

  // Guide related Locale Keys
  GUIDE_DESCRIPTION_HIDDEN: "guide_description_hidden",
  GUIDE_PREVIEW_RELEASE: "guide_preview_release",
  GUIDE_TIMEBAR_TODAY: "common_today",
  GUIDE_TITLE_HIDDEN: "guide_title_hidden",
  GUIDE_FILTER_FAVORITE: "guide_filter_favorite",
  GUIDE_FILTER_DRAMA: "guide_filter_drama",
  GUIDE_FILTER_MOVIES: "guide_filter_movies",
  GUIDE_FILTER_SPORTS: "guide_filter_sports",
  GUIDE_FILTER_KIDS: "guide_filter_kids",
  GUIDE_FILTER_PAST_NAV_HINT: "guide_filter_panel_past_guide_nav_hint",
  GUIDE_FILTER_NO_RESULTS_TITLE: "guide_filter_no_results_title",
  GUIDE_FILTER_NO_RESULTS_SUBTITLE: "guide_filter_no_results_subtitle",
  GUIDE_FILTERED_CHANNELS_FETCH_FAILED_TITLE:
    "guide_filtered_channels_fetch_failed_title",
  GUIDE_FILTERED_CHANNELS_FETCH_FAILED_SUBTITLE:
    "guide_filtered_channels_fetch_failed_subtitle",
  GUIDE_FILTER_FUTURE_NAV_HINT: "guide_filter_panel_future_guide_nav_hint",
  GUIDE_CHANNEL_SERVICE_UNAVAILABLE_MESSAGE:
    "guide_channel_service_unavailable_message",
  GUIDE_TUNE_TO_CHANNEL: "guide_tune_to_channel",
  GUIDE_JUMP_NOT_FOUND: "guide_jump_not_found",

  // Home Screen related Locale Keys
  HOME_MENU_STRIP_GUIDE: "nav_tab_guide",
  HOME_MENU_STRIP_HOME: "nav_tab_home",
  HOME_MENU_STRIP_MY_HUB: "nav_tab_my_hub",
  HOME_MENU_STRIP_SEARCH: "nav_tab_search",
  HOME_MENU_STRIP_SETTINGS: "nav_tab_settings",
  HOME_MENU_STRIP_SHOWS_AND_MOVIES: "nav_tab_shows_and_movies",
  HOME_MENU_STRIP_SPORTS: "nav_tab_sports",
  HOME_NOW_PLAYING_LABEL: "home_now_playing_label",
  HOME_TILE_PREVIEW_TYPE_MOVIE: "homescreen_tile_preview_type_movie",
  HOME_TILE_PREVIEW_TYPE_SHOW: "homescreen_tile_preview_type_show",

  // Shows & Movies screen
  SHOWS_AND_MOVIES_FILTER_FOR_YOU: "shows_and_movies_filter_for_you",
  SHOWS_AND_MOVIES_FILTER_ON_DEMAND: "shows_and_movies_filter_on_demand",
  SHOWS_AND_MOVIES_FILTER_SHOWS: "shows_and_movies_filter_shows",
  SHOWS_AND_MOVIES_FILTER_MOVIES: "shows_and_movies_filter_movies",
  SHOWS_AND_MOVIES_FILTER_TIVO_PLUS: "shows_and_movies_filter_tivo_plus",
  SHOWS_AND_MOVIES_GENRE_ALL: "shows_and_movies_genre_all",
  SHOWS_AND_MOVIES_GENRE_ACTION: "shows_and_movies_genre_action",
  SHOWS_AND_MOVIES_GENRE_DRAMA: "shows_and_movies_genre_drama",
  SHOWS_AND_MOVIES_GENRE_REALITY: "shows_and_movies_genre_reality",
  SHOWS_AND_MOVIES_GENRE_COMEDY: "shows_and_movies_genre_comedy",
  SHOWS_AND_MOVIES_GENRE_KIDS: "shows_and_movies_genre_kids",
  SHOWS_AND_MOVIES_SERVICE_UNAVAILABLE_TITLE:
    "shows_and_movies_service_unavailable_title",
  SHOWS_AND_MOVIES_SERVICE_UNAVAILABLE_MESSAGE:
    "shows_and_movies_service_unavailable_message",

  // Settings screen related Locale Keys
  SETTINGS_LABEL_MANAGE_PROFILES: "settings_label_manage_profiles",
  SETTINGS_LABEL_CREATE_PROFILE: "settings_label_create_profile",
  SETTINGS_LABEL_PARENTAL_CONTROL: "settings_label_parental_control",
  SETTINGS_LABEL_DEVICE_INFO: "settings_label_device_info",
  SETTINGS_LABEL_AUDIO_SETTINGS: "settings_label_audio_settings",
  SETTINGS_LABEL_REMOTE_SETTINGS: "settings_label_remote_settings",
  SETTINGS_LABEL_PRIVACY: "settings_label_privacy",
  SETTINGS_LABEL_HELP: "settings_label_help",
  SETTINGS_DESC_MANAGE_PROFILES: "settings_desc_manage_profiles",
  SETTINGS_DESC_PARENTAL_CONTROLS: "settings_desc_parental_controls",
  SETTINGS_DESC_SYSTEM_INFO: "settings_desc_system_info",
  SETTINGS_DESC_AUDIO_SETTINGS: "settings_desc_audio_settings",
  SETTINGS_DESC_REMOTE_SETTINGS: "settings_desc_remote_settings",
  SETTINGS_DESC_PRIVACY: "settings_desc_privacy",
  SETTINGS_DESC_HELP: "settings_desc_help",

  // Tile badge labels
  NEW_BADGE_LABEL: "new_badge_label",
  UPCOMING_BADGE_LABEL: "upcoming_badge_label",
  LIVE_BADGE_LABEL: "live_badge_label",
  WATCHED_BADGE_LABEL: "watched_badge_label",
  SAVED_BADGE_LABEL: "saved_badge_label",

  // Home Screen error dialog (A707)
  HOME_SCREEN_ERROR_TITLE: "home_screen_error_title",
  HOME_SCREEN_ERROR_MESSAGE: "home_screen_error_message",

  // Root error overlay — a screen that could not be loaded or rendered at all
  // (failed code-split chunk, unrecoverable render error). See
  // {@link RootErrorBoundary}.
  ROOT_ERROR_TITLE: "root_error_title",
  ROOT_ERROR_MESSAGE: "root_error_message",
  /**
   * "Something went wrong. Please try again." — generic body, used as the
   * fallback for {@link ROOT_ERROR_MESSAGE} until its translations land.
   */
  COMMON_ERROR_UNKNOWN: "common_error_unknown",

  // SOS overlay — shown once the session's silent-restart budget
  // (`restartLimitGuard`) is fully spent, so the app stops silently restarting
  // and asks the user to exit instead. See {@link RootErrorBoundary}.
  SOS_ERROR_TITLE: "sos_error_title",
  SOS_ERROR_MESSAGE: "sos_error_message",

  // Network error dialog
  NETWORK_ERROR_TITLE: "network_error_title",
  NETWORK_ERROR_MESSAGE: "network_error_message",
  NETWORK_ERROR_RETRY: "network_error_retry",
  // Exit confirmation dialog
  EXIT_DIALOG_TITLE: "exit_dialog_title",
  EXIT_DIALOG_MESSAGE: "exit_dialog_message",

  // Live TV Player related Locale Keys
  LIVE_TV_APP_SESSION_EXPIRED_MESSAGE: "live_tv_app_session_expired_message",
  LIVE_TV_APP_SESSION_EXPIRED_TITLE: "live_tv_app_session_expired_title",
  LIVE_TV_CANT_WATCH_SHOWS_MESSAGE: "live_tv_cant_watch_shows_message",
  LIVE_TV_GRACE_PERIOD_ENDED_ERROR_MESSAGE:
    "live_tv_grace_period_ended_error_message",
  LIVE_TV_GRACE_PERIOD_ENDED_ERROR_TITLE:
    "live_tv_grace_period_ended_error_title",
  LIVE_TV_INACTIVITY_OSD_MESSAGE: "live_tv_inactivity_osd_message",
  LIVE_TV_PLAYER_ENFORCEMENT_ERROR_MESSAGE:
    "live_tv_player_enforcement_error_message",
  LIVE_TV_PLAYER_ENFORCEMENT_ERROR_TITLE:
    "live_tv_player_enforcement_error_title",
  LIVE_TV_PLAYER_ERROR_TITLE: "live_tv_player_error_title",
  LIVE_TV_SUBSCRIPTION_REQUIRED_TITLE: "live_tv_subscription_required_title",
  PLAYER_BLACKLISTED_MESSAGE: "player_blacklisted_message",
  PLAYER_DEVICE_RESTRICTED_MESSAGE: "player_device_restricted_message",
  PLAYER_NETWORK_UNAVAILABLE_MESSAGE: "player_network_unavailable_message",
  PLAYER_OUT_OF_HOME_MESSAGE: "player_out_of_home_message",
  PLAYER_OUT_OF_REGION_MESSAGE: "player_out_of_region_message",
  PLAYER_UNSUBSCRIBED_MESSAGE: "player_unsubscribed_message",
  LIVE_TV_UNABLE_TO_PLAY_MESSAGE: "live_tv_unable_to_play_message",
  PLAYER_SOMETHING_WENT_WRONG_TITLE: "player_something_went_wrong_title",
  PLAYER_SOMETHING_WENT_WRONG_MESSAGE: "player_something_went_wrong_message",
  PLAYER_ERROR_CANT_PLAY_TITLE: "player_error_cant_play_title",

  // Streaming-session error messages (mapped from session-manager error codes)
  SESSION_ERROR_NOT_AUTHORIZED_MESSAGE: "session_error_not_authorized_message",
  SESSION_ERROR_TOO_MANY_STREAMS_MESSAGE:
    "session_error_too_many_streams_message",
  SESSION_ERROR_TOO_MANY_STREAMS_FOR_RESOURCE_MESSAGE:
    "session_error_too_many_streams_for_resource_message",
  SESSION_COMMON_PLAYBACK_ERROR: "session_common_playback_error",

  // Recording overlay related Locale Keys
  PLAYER_RECORDING_SET_TITLE: "player_recording_set_title",
  PLAYER_RECORDING_OVERLAY_MESSAGE: "player_recording_overlay_message",
  PLAYER_RECORD_ALL_EPISODES: "player_record_all_episodes",
  PLAYER_RECORD_THIS_EPISODE_ONLY: "player_record_this_episode_only",
  PLAYER_RECORDING_SCHEDULED_THIS_PROGRAM_TOAST:
    "player_recording_scheduled_this_program_toast",
  PLAYER_RECORDING_FUTURE_EPISODES_MESSAGE_WITH_TITLE:
    "player_recording_future_episodes_message_with_title",
  PLAYER_RECORDING_CONTINUE_BUTTON: "player_recording_continue_button",
  PLAYER_RECORDING_MANAGE_BUTTON: "player_recording_manage_button",
  PLAYER_RECORDING_STORAGE_OVERLAY_TITLE:
    "player_recording_storage_overlay_title",
  PLAYER_RECORDING_STORAGE_OVERLAY_MESSAGE:
    "player_recording_storage_overlay_message",
  PLAYER_ERROR_UNAVAILABLE_MESSAGE: "player_error_unavailable_message",
  PLAYER_ERROR_DRM_OUTPUT_PROTECTION_MESSAGE:
    "player_error_drm_output_protection_message",
  PLAYER_ERROR_DRM_SECURITY_MESSAGE: "player_error_drm_security_message",
  PLAYER_ERROR_BAD_INPUT_MESSAGE: "player_error_bad_input_message",
  PLAYER_ERROR_KNOWN_ISSUE_MESSAGE: "player_error_known_issue_message",
  PLAYER_ERROR_OPEN_READ_FAILED_MESSAGE:
    "player_error_open_read_failed_message",
  PLAYER_ERROR_KNOWN_ISSUE_ALL_PROGRAMS_MESSAGE:
    "player_error_known_issue_all_programs_message",
  PLAYER_ERROR_DECODER_QUERY_FAILED_MESSAGE:
    "player_error_decoder_query_failed_message",
  PLAYER_ERROR_DECODER_INIT_FAILED_MESSAGE:
    "player_error_decoder_init_failed_message",
  PLAYER_ERROR_DECODER_CRYPTO_FAILED_MESSAGE:
    "player_error_decoder_crypto_failed_message",
  PLAYER_ERROR_SUBTITLE_DECODER_FAILED_MESSAGE:
    "player_error_subtitle_decoder_failed_message",
  PLAYER_ERROR_RENDER_FAILED_MESSAGE: "player_error_render_failed_message",
  PLAYER_ERROR_DRM_SYSTEM_ERROR_MESSAGE:
    "player_error_drm_system_error_message",
  PLAYER_ERROR_DRM_SCHEME_UNSUPPORTED_MESSAGE:
    "player_error_drm_scheme_unsupported_message",
  PLAYER_ERROR_DRM_INCOMPATIBLE_CONTENT_MESSAGE:
    "player_error_drm_incompatible_content_message",
  PLAYER_ERROR_DRM_LICENSE_EXPIRED_MESSAGE:
    "player_error_drm_license_expired_message",
  PLAYER_ERROR_DRM_LICENSE_UNAVAILABLE_MESSAGE:
    "player_error_drm_license_unavailable_message",
  PLAYER_ERROR_DRM_RESTRICTED_MESSAGE: "player_error_drm_restricted_message",
  PLAYER_ERROR_GENERIC_TITLE: "player_error_generic_title",
  PLAYER_ERROR_DECODER_ERROR_TITLE: "player_error_decoder_error_title",
  PLAYER_ERROR_DRM_TITLE: "player_error_drm_title",
  PLAYER_ERROR_UNAVAILABLE_TITLE: "player_error_unavailable_title",
  PLAYER_ERROR_DRM_SYSTEM_ERROR_TITLE: "player_error_drm_system_error_title",
  PLAYER_ERROR_DRM_SCHEME_UNSUPPORTED_TITLE:
    "player_error_drm_scheme_unsupported_title",
  PLAYER_ERROR_DRM_INCOMPATIBLE_CONTENT_TITLE:
    "player_error_drm_incompatible_content_title",
  PLAYER_ERROR_DRM_LICENSE_EXPIRED_TITLE:
    "player_error_drm_license_expired_title",
  PLAYER_ERROR_DRM_LICENSE_UNAVAILABLE_TITLE:
    "player_error_drm_license_unavailable_title",
  PLAYER_ERROR_DRM_RESTRICTED_TITLE: "player_error_drm_restricted_title",

  // Playback Restriction Toast related Locale Keys
  PLAYBACK_PAUSE_NOT_ALLOWED_MESSAGE: "playback_control_pause_not_allowed_msg",
  PLAYBACK_NOT_FAST_FORWARDABLE_MESSAGE: "playback_not_fast_forwardable",
  PLAYBACK_NOT_REWINDABLE_MESSAGE: "playback_not_rewindable",
  PLAYBACK_CHANNEL_CHANGE_LIVE_ONLY_MESSAGE:
    "playback_channel_change_available_live_only",
  PLAYBACK_CANT_STREAM_SHOW: "playback_cant_stream_show",
  PLAYBACK_CANT_WATCH_NOW: "playback_cant_watch_now",
  PLAYBACK_CANT_WATCH_SHOWS: "playback_cant_watch_shows",
  PLAYBACK_ERROR_NO_SESSION_MESSAGE: "playback_error_no_session_message",
  PLAYBACK_UNKNOWN_LOCATION_ERROR: "playback_unknown_location_error_message",
  PLAYBACK_PROHIBITED_MESSAGE: "playback_prohibited_message",

  // Playback Pause Timeout Dialog related Locale Keys
  PAUSE_TIMEOUT_DIALOG_TITLE: "playback_pause_timeout_title",
  PAUSE_TIMEOUT_ACTION_RESUME: "playback_pause_timeout_resume",
  PAUSE_TIMEOUT_ACTION_STOP: "playback_pause_timeout_stop",

  // Sign-In related Locale Keys
  SIGN_IN_CODE_DISPLAY_INSTRUCTION: "sign_in_code_display_instruction",
  SIGN_IN_CODE_EXPIRED_MESSAGE: "sign_in_code_expired_message",
  SIGN_IN_ERROR_TITLE: "sign_in_error_title",
  /**
   * A463 (`POST v2/provisionDevices` 403 `AUTHS.TOO_MANY_DEVICES`), copied
   * from the error-code catalog's TOO_MANY_DEVICES_TITLE / _BODY /
   * _BODY_NEUTRAL. The neutral body is used when branding has no support
   * name or phone number to substitute.
   */
  SIGN_IN_TOO_MANY_DEVICES_TITLE: "sign_in_too_many_devices_title",
  SIGN_IN_TOO_MANY_DEVICES_MESSAGE: "sign_in_too_many_devices_message",
  SIGN_IN_TOO_MANY_DEVICES_MESSAGE_NEUTRAL:
    "sign_in_too_many_devices_message_neutral",
  SIGN_IN_GET_NEW_CODE: "sign_in_get_new_code",
  SIGN_IN_HEADER_TITLE: "sign_in_header_title",
  SIGN_IN_HELP_TEXT: "sign_in_help_text",
  SIGN_IN_FORGOT_PASSWORD_LINK: "sign_in_forgot_password_link",
  SIGN_IN_MOBILE_DIALOG_TITLE: "sign_in_mobile_dialog_title",
  SIGN_IN_MOBILE_DIALOG_MESSAGE: "sign_in_mobile_dialog_message",
  SIGN_IN_MOBILE_DIALOG_GET_APP_BUTTON: "sign_in_mobile_dialog_get_app_button",
  SIGN_IN_NO_AUTH_METHOD_AVAILABLE: "sign_in_no_auth_method_available",
  SIGN_IN_RECOVER_PASSWORD_TITLE: "sign_in_recover_password_title",
  SIGN_IN_REQUEST_NEW_LINK_TITLE: "sign_in_request_new_link_title",
  SIGN_IN_RECOVERY_EMAIL_LABEL: "sign_in_recovery_email_label",
  SIGN_IN_SEND_LINK_BUTTON: "sign_in_send_link_button",
  SIGN_IN_FORGOT_EMAIL_ADDRESS_LINK: "sign_in_forgot_email_address_link",
  SIGN_IN_FORGOT_EMAIL_ADDRESS_TITLE: "sign_in_forgot_email_address_title",
  SIGN_IN_FORGOT_EMAIL_ADDRESS_MESSAGE: "sign_in_forgot_email_address_message",
  SIGN_IN_LINK_SENT_TITLE: "sign_in_link_sent_title",
  SIGN_IN_LINK_SENT_MESSAGE: "sign_in_link_sent_message",
  SIGN_IN_LINK_EXPIRED_TITLE: "sign_in_link_expired_title",
  SIGN_IN_LINK_EXPIRED_MESSAGE: "sign_in_link_expired_message",
  SIGN_IN_LINK_EXPIRED_REQUEST_NEW_LINK_ACTION:
    "sign_in_link_expired_request_new_link_action",
  SIGN_IN_CREATE_PASSWORD_TITLE: "sign_in_create_password_title",
  SIGN_IN_RESET_PASSWORD_TITLE: "sign_in_reset_password_title",
  SIGN_IN_RESET_PASSWORD_NEW_LABEL: "sign_in_reset_password_new_label",
  SIGN_IN_RESET_PASSWORD_CONFIRM_LABEL: "sign_in_reset_password_confirm_label",
  SIGN_IN_RESET_PASSWORD_SUBMIT_BUTTON: "sign_in_reset_password_submit_button",
  SIGN_IN_RESET_PASSWORD_INCORRECT_TITLE:
    "sign_in_reset_password_incorrect_title",
  SIGN_IN_RESET_PASSWORD_INCORRECT_MESSAGE:
    "sign_in_reset_password_incorrect_message",
  SIGN_IN_RESET_PASSWORD_MISMATCH_MESSAGE:
    "sign_in_reset_password_mismatch_message",
  SIGN_IN_RESET_PASSWORD_SUCCESS_TITLE: "sign_in_reset_password_success_title",
  SIGN_IN_RESET_PASSWORD_SUCCESS_MESSAGE:
    "sign_in_reset_password_success_message",
  SIGN_IN_RESET_PASSWORD_REQUIREMENTS: "sign_in_reset_password_requirements",
  SIGN_IN_USE_QR_CODE_INSTRUCTION: "sign_in_use_qr_code_instruction",
  SIGN_IN_TAB_USER_ID_PASSWORD: "sign_in_tab_user_id_password",
  SIGN_IN_TAB_USE_CODE: "sign_in_tab_use_code",
  SIGN_IN_USER_ID_LABEL: "sign_in_user_id_label",
  SIGN_IN_SUBMIT_BUTTON: "sign_in_submit_button",
  SIGN_IN_SIGNING_IN_ALT: "sign_in_signing_in_alt",
  SIGN_IN_HEADER_LOGO_ALT: "sign_in_header_logo_alt",
  SIGN_IN_QR_CODE_ALT: "sign_in_qr_code_alt",
  SIGN_IN_PASSWORD_LABEL: "sign_in_password_label",
  SIGN_IN_PASSWORD_SHOW_BUTTON: "sign_in_password_show_button",
  SIGN_IN_PASSWORD_HIDE_BUTTON: "sign_in_password_hide_button",
  SIGN_IN_PASSWORD_SHOW_PASSWORD_LABEL: "sign_in_password_show_password_label",
  SIGN_IN_PASSWORD_HIDE_PASSWORD_LABEL: "sign_in_password_hide_password_label",
  SIGN_IN_PASSWORD_SHOW_LABEL: "sign_in_password_show_label",
  SIGN_IN_PASSWORD_HIDE_LABEL: "sign_in_password_hide_label",
  SIGN_IN_CONFIRM_PASSWORD_SHOW_LABEL: "sign_in_confirm_password_show_label",
  SIGN_IN_CONFIRM_PASSWORD_HIDE_LABEL: "sign_in_confirm_password_hide_label",

  /**
   * Fallback bodies for a failed sign-in service call — what the error alert
   * shows when the failure carried no text of its own.
   *
   * These reach the screen: `extractServiceErrorMessage` returns its
   * `fallbackMessage` verbatim when the error has no description, no envelope
   * message and no `Error.message`. They were English literals at each call
   * site until now, several of them internal jargon ("ensureValidToken
   * failed"), so the copy here is written for a user rather than transliterated.
   *
   * Read through `localizedErrorFallback`, which substitutes
   * {@link COMMON_ERROR_UNKNOWN} while a key has no string in the active
   * bundle, so none of these can render blank.
   */
  SIGN_IN_INITIALIZATION_ERROR_MESSAGE: "sign_in_initialization_error_message",
  SIGN_IN_CREDENTIALS_ERROR_MESSAGE: "sign_in_credentials_error_message",
  SIGN_IN_DEVICE_IDENTITY_RESET_MESSAGE:
    "sign_in_device_identity_reset_message",
  SIGN_IN_CODE_FLOW_ERROR_MESSAGE: "sign_in_code_flow_error_message",
  SIGN_IN_CODE_POLLING_ERROR_MESSAGE: "sign_in_code_polling_error_message",
  SIGN_IN_CODE_ACTIVATION_ERROR_MESSAGE:
    "sign_in_code_activation_error_message",
  SIGN_IN_AUTO_LOGIN_ERROR_MESSAGE: "sign_in_auto_login_error_message",
  SIGN_IN_SESSION_REFRESH_ERROR_MESSAGE:
    "sign_in_session_refresh_error_message",
  SIGN_IN_SESSION_EXPIRED_ERROR_MESSAGE:
    "sign_in_session_expired_error_message",
  SIGN_IN_SESSION_RESTORE_ERROR_MESSAGE:
    "sign_in_session_restore_error_message",
  SIGN_IN_RECOVERY_EMAIL_REQUIRED_MESSAGE:
    "sign_in_recovery_email_required_message",
  SIGN_IN_RESET_LINK_SEND_ERROR_MESSAGE:
    "sign_in_reset_link_send_error_message",
  SIGN_IN_RESET_LINK_INVALID_ERROR_MESSAGE:
    "sign_in_reset_link_invalid_error_message",
  SIGN_IN_RESET_PASSWORD_UPDATE_ERROR_MESSAGE:
    "sign_in_reset_password_update_error_message",
  SIGN_IN_RESET_SESSION_INVALID_MESSAGE:
    "sign_in_reset_session_invalid_message",
  FORGOT_PASSWORD_API_FETCH_FAILED: "forgot_password_api_fetch_failed",
  FORGOT_PASSWORD_DEMARSHALLED_ERROR_MESSAGE:
    "forgot_password_demarshalled_error_message",
  FORGOT_PASSWORD_PARTIAL_RESPONSE_MESSAGE:
    "forgot_password_partial_response_message",
  VALIDATE_EMAIL_TOKEN_API_FETCH_FAILED:
    "validate_email_token_api_fetch_failed",
  VALIDATE_EMAIL_TOKEN_DEMARSHALLED_ERROR_MESSAGE:
    "validate_email_token_demarshalled_error_message",
  VALIDATE_EMAIL_TOKEN_MISSING_EMAIL_MESSAGE:
    "validate_email_token_missing_email_message",
  VALIDATE_EMAIL_TOKEN_MISSING_IDENTITY_ID_MESSAGE:
    "validate_email_token_missing_identity_id_message",
  VALIDATE_EMAIL_TOKEN_MISSING_PARTNER_CUSTOMER_ID_MESSAGE:
    "validate_email_token_missing_partner_customer_id_message",
  VALIDATE_EMAIL_TOKEN_MISSING_MSO_NAME_MESSAGE:
    "validate_email_token_missing_mso_name_message",
  UPDATE_PASSWORD_API_FETCH_FAILED: "update_password_api_fetch_failed",
  UPDATE_PASSWORD_DEMARSHALLED_ERROR_MESSAGE:
    "update_password_demarshalled_error_message",
  UPDATE_PASSWORD_PARTIAL_RESPONSE_MESSAGE:
    "update_password_partial_response_message",
  UPDATE_PASSWORD_STATUS_FAILURE_MESSAGE:
    "update_password_status_failure_message",

  // Temporary Service Connection Issue related Locale Keys
  TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION:
    "temporary_service_connection_issue_description",
  TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE:
    "temporary_service_connection_issue_title",

  // Claim API related Locale Keys
  CLAIM_API_FETCH_FAILED: "claim_api_fetch_failed",

  // idpJwtToken API related Locale Keys
  IDP_JWT_TOKEN_API_FETCH_FAILED: "idp_jwt_token_api_fetch_failed",

  // Bind API related Locale Keys
  BIND_API_FETCH_FAILED: "bind_api_fetch_failed",

  DEVICE_BINDING_API_FETCH_FAILED: "device_binding_api_fetch_failed",

  // Device Binding DELETE related Locale Keys
  DEVICE_BINDING_DELETE_BAD_REQUEST_PARAMETERS:
    "device_binding_delete_bad_request_parameters",
  DEVICE_BINDING_DELETE_RESOURCE_NOT_FOUND:
    "device_binding_delete_resource_not_found",
  DEVICE_BINDING_DELETE_NOT_FOUND_TO_REMOVE:
    "device_binding_delete_not_found_to_remove",
  DEVICE_BINDING_DELETE_GA_ERROR: "device_binding_delete_ga_error",
  DEVICE_BINDING_DELETE_DYNAMODB_ERROR: "device_binding_delete_dynamodb_error",
  DEVICE_BINDING_DELETE_INTEGRATION_TIMEOUT:
    "device_binding_delete_integration_timeout",
  DEVICE_BINDING_DELETE_WATCHDOG_ERROR: "device_binding_delete_watchdog_error",
  DEVICE_BINDING_DELETE_APIGW_ERROR: "device_binding_delete_apigw_error",
  DEVICE_BINDING_DELETE_CONNECTION_REFUSED:
    "device_binding_delete_connection_refused",
  DEVICE_BINDING_DELETE_CONNECTION_FAILED:
    "device_binding_delete_connection_failed",
  DEVICE_BINDING_DELETE_BAD_REQUEST: "device_binding_delete_bad_request",
  DEVICE_BINDING_DELETE_BAD_RESPONSE: "device_binding_delete_bad_response",
  DEVICE_BINDING_DELETE_SHORT_RESPONSE: "device_binding_delete_short_response",
  DEVICE_BINDING_DELETE_UNKNOWN_ERROR: "device_binding_delete_unknown_error",
  DEVICE_BINDING_DELETE_DEMARSHAL_ERROR:
    "device_binding_delete_demarshal_error",
  // Profiles related Locale Keys
  PROFILE_WHO_IS_WATCHING_TITLE: "profile_who_is_watching_title",
  PROFILE_SKIP_SELECTED_PROFILE: "profile_skip_selected_profile",
  PROFILE_ADMIN_LABEL: "profile_admin_label",
  PROFILE_ERROR_ACCESS_DENIED_MESSAGE: "profile_error_access_denied_message",
  PROFILE_ERROR_AUTHENTICATION_MESSAGE: "profile_error_authentication_message",
  PROFILE_ERROR_DELETE_MESSAGE: "profile_error_delete_message",
  PROFILE_ERROR_LIMIT_REACHED_MESSAGE: "profile_error_limit_reached_message",
  PROFILE_ERROR_NETWORK_MESSAGE: "profile_error_network_message",
  PROFILE_ERROR_NOT_FOUND_MESSAGE: "profile_error_not_found_message",
  PROFILE_ERROR_SAVE_MESSAGE: "profile_error_save_message",
  PROFILE_ERROR_SELECT_MESSAGE: "profile_error_select_message",
  PROFILE_ERROR_SERVICE_MESSAGE: "profile_error_service_message",
  PROFILE_ERROR_TITLE: "profile_error_title",
  PROFILE_LOAD_FAILED_FALLBACK: "profile_load_failed_fallback",
  PROFILE_SELECTION_PREFERENCE_LOAD_FAILED_FALLBACK:
    "profile_selection_preference_load_failed_fallback",
  PROFILE_SELECTION_PREFERENCE_SAVE_FAILED_FALLBACK:
    "profile_selection_preference_save_failed_fallback",
  PROFILE_FETCH_FAILED_FALLBACK: "profile_fetch_failed_fallback",
  PROFILE_PARTNER_CONFIG_LOAD_FAILED_FALLBACK:
    "profile_partner_config_load_failed_fallback",
  PROFILE_CREATE_FAILED_FALLBACK: "profile_create_failed_fallback",
  PROFILE_UPDATE_FAILED_FALLBACK: "profile_update_failed_fallback",
  PROFILE_DELETE_FAILED_FALLBACK: "profile_delete_failed_fallback",
  PROFILE_SET_SELECTED_FAILED_FALLBACK: "profile_set_selected_failed_fallback",
  PROFILE_ACTIVATE_FAILED_FALLBACK: "profile_activate_failed_fallback",

  // Manage Profiles screen related Locale Keys
  MANAGE_PROFILES_TITLE: "manage_profiles_title",
  MANAGE_PROFILES_ADD_NEW: "manage_profiles_add_new",
  MANAGE_PROFILES_EDIT: "manage_profiles_edit",
  MANAGE_PROFILES_REMOVE: "manage_profiles_remove",
  MANAGE_PROFILES_LIMIT_REACHED: "manage_profiles_limit_reached",

  // Remove Profile confirmation dialog related Locale Keys
  REMOVE_PROFILE_TITLE: "remove_profile_title",
  REMOVE_PROFILE_MESSAGE: "remove_profile_message",
  REMOVE_PROFILE_CONFIRM: "remove_profile_confirm",

  // Profile Details screen related Locale Keys
  PROFILE_DETAILS_TITLE: "profile_details_title",
  PROFILE_DETAILS_NAME_LABEL: "profile_details_name_label",
  PROFILE_DETAILS_NAME_PLACEHOLDER: "profile_details_name_placeholder",
  PROFILE_DETAILS_AVATAR_ALT: "profile_details_avatar_alt",
  PROFILE_DETAILS_SAVE: "profile_details_save",
  PROFILE_DETAILS_CANCEL: "profile_details_cancel",
  PROFILE_DETAILS_VALIDATION_ERROR: "profile_details_validation_error",

  // Avatar Selection overlay related Locale Keys
  AVATAR_SELECTION_TITLE: "avatar_selection_title",

  // Search Screen related Locale Keys
  SEARCH_INPUT_PLACEHOLDER: "search_input_placeholder",
  SEARCH_RECENTLY_SEARCHED: "recently_searched",
  SEARCH_CLEAR_RECENTS: "search_clear_recents",
  SEARCH_NO_RESULTS_MESSAGE: "search_no_results_message",
  SEARCH_ERROR_MESSAGE: "search_error_message",

  // Season/episode suffix — "S{{seasonNumber}} E{{episodeNumber}}"
  CONTENT_ACTION_SUFFIX_SEASON_EPISODE: "content_action_suffix_season_episode",

  // Recordings
  NO_OF_RECORDINGS: "no_of_recordings",

  // My Hub — errors (UAW-2412). All carry the {{errorCode}} composite code
  // from MyHubErrorMapper. _MESSAGE is shown inline where a failed list/detail
  // fetch would otherwise render (MyHubSavedList.tsx); _TOAST is the
  // transient toast shown alongside it;
  // _NETWORK_ERROR_MESSAGE replaces either of those when the failure is
  // specifically a connectivity one (offline, connection dropped/refused).
  MY_HUB_ERROR_MESSAGE: "my_hub_error_message",
  MY_HUB_NETWORK_ERROR_MESSAGE: "my_hub_network_error_message",
  MY_HUB_REQUEST_FAILED_TOAST: "my_hub_request_failed_toast",

  // My Hub — Saved tab (Figma: My Hub Detail, node 115:780)
  MY_HUB_SAVED_SUMMARY_COUNT: "my_hub_saved_summary_count",
  MY_HUB_SAVED_SUMMARY_HOURS: "my_hub_saved_summary_hours",
  MY_HUB_SAVED_ON: "my_hub_saved_on",
  MY_HUB_SAVED_EXPIRES_ON: "my_hub_saved_expires_on",
  MY_HUB_SAVED_THUMBNAIL_CAPTION: "my_hub_saved_thumbnail_caption",
  MY_HUB_SAVED_DURATION_HOUR: "my_hub_saved_duration_hour",
  MY_HUB_SAVED_DURATION_HOURS: "my_hub_saved_duration_hours",
  MY_HUB_SAVED_DURATION_MINUTES: "my_hub_saved_duration_minutes",
  MY_HUB_SAVED_EMPTY_MESSAGE: "my_hub_saved_empty_message",

  // My Hub — Deleted tab (Figma: My Hub Detail, node 71:2130). The tab shares
  // the Saved tab's flat list, so it reuses MY_HUB_SAVED_EXPIRES_ON and only
  // adds the copy that actually differs.
  MY_HUB_DELETED_SUMMARY_COUNT: "my_hub_deleted_summary_count",
  MY_HUB_DELETED_SUMMARY_USED: "my_hub_deleted_summary_used",
  MY_HUB_DELETED_ON: "my_hub_deleted_on",
  MY_HUB_DELETED_EMPTY_MESSAGE: "my_hub_deleted_empty_message",
  MY_HUB_DELETED_DELETE_ALL: "my_hub_deleted_delete_all",
  MY_HUB_DELETED_PERMANENT_CONFIRM_TITLE:
    "my_hub_deleted_permanent_confirm_title",
  MY_HUB_DELETED_PERMANENT_CONFIRM_MESSAGE:
    "my_hub_deleted_permanent_confirm_message",
  MY_HUB_DELETED_PERMANENT_CONFIRM_ACCEPT:
    "my_hub_deleted_permanent_confirm_accept",
  MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_TITLE:
    "my_hub_deleted_permanent_item_confirm_title",
  MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_MESSAGE:
    "my_hub_deleted_permanent_item_confirm_message",
  MY_HUB_DELETED_PERMANENT_ITEM_CONFIRM_ACCEPT:
    "my_hub_deleted_permanent_item_confirm_accept",
  MY_HUB_SORT_BY_LABEL: "my_hub_sort_by",
  MY_HUB_SORT_OPTION_RECENT: "my_hub_sort_recent",
  MY_HUB_SORT_OPTION_ALPHABETICAL: "my_hub_sort_a_z",
  MY_HUB_QUOTA_FREE_LABEL: "my_hub_storage_free",

  // My Hub — Manage series recordings screen
  // (Figma: My Hub  Detail, node 71:4516 "Managed recordings -series")
  MY_HUB_SERIES_EPISODE_COUNT: "my_hub_series_episode_count",
  MY_HUB_SERIES_TOTAL_HOURS: "my_hub_series_total_hours",
  MY_HUB_SERIES_AIRED_TODAY: "my_hub_series_aired_today",
  MY_HUB_SERIES_AIRED_ON_CHANNEL: "my_hub_series_aired_on_channel",
  MY_HUB_SERIES_EMPTY_MESSAGE: "my_hub_series_empty_message",
  MY_HUB_SERIES_LIST_HEADING_RECORDED: "my_hub_series_list_heading_recorded",
  MY_HUB_SERIES_TITLE_RECORDED: "my_hub_series_title_recorded",
  MY_HUB_SERIES_TITLE_SCHEDULED: "my_hub_series_title_scheduled",
  MY_HUB_SERIES_FOLDER_RECORDING_OPTIONS:
    "my_hub_series_folder_recording_options",
  MY_HUB_SERIES_FOLDER_CANCEL_RECORDINGS:
    "my_hub_series_folder_cancel_recordings",
  MY_HUB_SERIES_FOLDER_DELETE_ALL: "my_hub_series_folder_delete_all",
  MY_HUB_SERIES_FOLDER_START_RECORDING: "my_hub_series_folder_start_recording",
  MY_HUB_SERIES_CANCEL_CONFIRM_TITLE: "my_hub_series_cancel_confirm_title",
  MY_HUB_SERIES_CANCEL_CONFIRM_MESSAGE: "my_hub_series_cancel_confirm_message",
  MY_HUB_SERIES_CANCEL_CONFIRM_ACCEPT: "my_hub_series_cancel_confirm_accept",
  MY_HUB_SERIES_DELETE_CONFIRM_TITLE: "my_hub_series_delete_confirm_title",
  MY_HUB_SERIES_DELETE_CONFIRM_MESSAGE: "my_hub_series_delete_confirm_message",
  MY_HUB_SERIES_DELETE_CONFIRM_ACCEPT: "my_hub_series_delete_confirm_accept",

  // My Hub — Scheduled tab drill-ins
  // (Figma: My Hub Detail, node 407:1437 / 407:1709 "Scheduled _ series")
  MY_HUB_SCHEDULED_SINGLE_TITLE: "my_hub_scheduled_single_title",

  // Series Recording Overlay
  MY_HUB_SERIES_RECORDING_OPTIONS_KEEP_AT_MOST_DESCRIPTION:
    "my_hub_series_recording_option_keep_at_most_description",
  MY_HUB_SERIES_RECORDING_OPTIONS_CHANNELS_DESCRIPTION:
    "my_hub_series_recording_option_channels_description",
  MY_HUB_SERIES_RECORDING_OPTIONS_START_RECORDING_DESCRIPTION:
    "my_hub_series_recording_option_start_recording_description",
  MY_HUB_SERIES_RECORDING_OPTIONS_STOP_RECORDING_DESCRIPTION:
    "my_hub_series_recording_option_stop_recording_description",

  // ContentCard action-button labels (used as the accessible name, the tooltip,
  // and the visible label on the icon-plus-text form)
  CONTENT_CARD_ACTION_PLAY: "content_card_action_play",
  CONTENT_CARD_ACTION_DELETE: "content_card_action_delete",
  CONTENT_CARD_ACTION_DETAILS: "content_card_action_details",
  CONTENT_CARD_ACTION_UNSAVE: "content_card_action_unsave",
  CONTENT_CARD_ACTION_SAVE: "content_card_action_save",
  CONTENT_CARD_ACTION_UNDELETE: "content_card_action_undelete",
  CONTENT_CARD_ACTION_PERMANENTLY_DELETE:
    "content_card_action_permanently_delete",
  CONTENT_CARD_ACTION_STOP: "content_card_action_stop",
  CONTENT_CARD_ACTION_EXTEND: "content_card_action_extend",

  // ── Content Detail screen ────────────────────────────────────────────
  // Every Watch/Schedule pill label the action catalog can resolve to. The
  // catalog maps many distinct ActionKernelTypes onto one label, so this is a
  // list of *labels*, not of kernels — see
  // src/viewModel/contentDetail/contentDetailStrings.ts for the kernel→key
  // tables. Mirrors Android's DetailsScreenUiStrings.
  CONTENT_ACTION_LABEL_WATCH_ON_NOW: "content_action_label_watch_on_now",
  CONTENT_ACTION_LABEL_RECORDING: "content_action_label_recording",
  CONTENT_ACTION_LABEL_RESUME: "content_action_label_resume",
  CONTENT_ACTION_LABEL_CATCHUP: "content_action_label_catchup",
  CONTENT_ACTION_LABEL_ON_DEMAND: "content_action_label_on_demand",
  CONTENT_ACTION_LABEL_PREVIEW: "content_action_label_preview",
  CONTENT_ACTION_LABEL_WATCH: "content_action_label_watch",
  CONTENT_ACTION_LABEL_PLAY_NEXT: "content_action_label_play_next",
  CONTENT_ACTION_LABEL_STREAMING_OPTIONS:
    "content_action_label_streaming_options",
  CONTENT_ACTION_LABEL_CATCHUP_OPTIONS: "content_action_label_catchup_options",
  CONTENT_ACTION_LABEL_ON_DEMAND_OPTIONS:
    "content_action_label_on_demand_options",
  CONTENT_RECORDING_KEEP_UNTIL: "content_recording_keep_until",
  CONTENT_RECORDING_START: "content_recording_start",
  CONTENT_RECORDING_STOP: "content_recording_stop",
  CONTENT_RECORDING_TIME_CHANNEL: "content_recording_time_channel",
  CONTENT_ACTION_LABEL_STREAMING_OPTIONS_SUBTITLE:
    "content_action_label_streaming_options_subtitle",
  /** Shared with the player/trickplay "Record" label — the string is identical. */
  CONTENT_ACTION_LABEL_RECORD: "common_record",
  CONTENT_ACTION_LABEL_RECORD_AGAIN: "content_action_label_record_again",
  CONTENT_ACTION_LABEL_RECORD_SERIES: "content_action_label_record_series",
  CONTENT_ACTION_LABEL_MODIFY_RECORDING:
    "content_action_label_modify_recording",
  CONTENT_ACTION_LABEL_RECOVER_RECORDING:
    "content_action_label_recover_recording",
  CONTENT_ACTION_LABEL_STOP_AND_DELETE_RECORDING:
    "content_action_label_stop_and_delete_recording",
  CONTENT_ACTION_LABEL_CANCEL_RECORDING:
    "content_action_label_cancel_recording",
  CONTENT_ACTION_LABEL_DELETE_RECORDING:
    "content_action_label_delete_recording",
  CONTENT_ACTION_LABEL_MANAGE_RECORDINGS:
    "content_action_label_manage_recordings",
  /**
   * Figma (Content Detail, node 2198-5311) labels this pill "Bookmark" with a
   * heart icon, while the shipped EN string is "Add Favorite". The bundle is
   * the source of truth for copy — changing the wording is a locale change,
   * not a code change — so the key stays and the label follows the bundle.
   */
  CONTENT_ACTION_LABEL_ADD_FAVORITE: "content_action_label_add_favorite",
  CONTENT_ACTION_LABEL_REMOVE_FAVORITE: "content_action_label_remove_favorite",

  // Explore section headers (Figma renders these uppercase via CSS, so the
  // bundle keeps them in title case).
  CONTENT_EXPLORE_TAB_EPISODES: "content_explore_tab_episodes",
  CONTENT_EXPLORE_TAB_EPISODE_GUIDE: "content_explore_tab_episode_guide",
  CONTENT_EXPLORE_TAB_UPCOMING: "content_explore_tab_upcoming",
  CONTENT_EXPLORE_TAB_CAST: "content_explore_tab_cast",
  CONTENT_EXPLORE_TAB_MAY_ALSO_LIKE: "content_explore_tab_may_also_like",

  // Season picker above the episode / upcoming rails.
  CONTENT_EPISODE_GUIDE_TAB_SEASON: "content_episode_guide_tab_season",
  CONTENT_EPISODE_GUIDE_TAB_YEAR: "content_episode_guide_tab_year",
  CONTENT_EPISODE_GUIDE_TAB_EXTRA: "content_episode_guide_tab_extra",

  // Card metadata + hero metadata lines. The "S{{seasonNumber}} E{{episodeNumber}}"
  // template is already declared above as CONTENT_ACTION_SUFFIX_SEASON_EPISODE
  // (My Hub uses the same string) — the rail card metadata reuses that key.
  CONTENT_NEXT_AIRING: "content_next_airing",
  CONTENT_WATCH_ON_PROVIDER: "watch_on_provider",

  CONTENT_TYPE_LABEL_MOVIE: "content_type_label_movie",
  CONTENT_TYPE_LABEL_SERIES: "content_type_label_series",
  CONTENT_TYPE_LABEL_EPISODE: "content_type_label_episode",
  CONTENT_TYPE_LABEL_PROGRAM: "content_type_label_program",
  CONTENT_TYPE_LABEL_GAME: "content_type_label_game",

  CONTENT_BADGE_LABEL_HD: "content_badge_label_hd",
  CONTENT_BADGE_LABEL_SD: "content_badge_label_sd",
  CONTENT_BADGE_LABEL_UHD: "content_badge_label_uhd",
  CONTENT_BADGE_LABEL_NEW: "content_badge_label_new",
  CONTENT_BADGE_LABEL_LIVE: "content_badge_label_live",

  // Per-section empty / error states (§3.3 SectionState).
  CONTENT_UPCOMING_EMPTY_MESSAGE: "content_upcoming_empty_message",
  CONTENT_EPISODES_EMPTY_MESSAGE: "content_episodes_empty_message",
  CONTENT_CAST_EMPTY_MESSAGE: "content_cast_empty_message",
  CONTENT_NO_ACTIONS_AVAILABLE: "content_no_actions_available",

  // Generic async-state strings reused by the screen and every section.
  ERROR_GENERIC_RETRY: "error_generic_retry",
  ERROR_GENERIC_FAILED_TO_LOAD: "error_generic_failed_to_load",
  ERROR_GENERIC_FAILED_TO_LOAD_MORE: "error_generic_failed_to_load_more",
  ERROR_GENERIC_NO_NETWORK: "error_generic_no_network",
  ERROR_GENERIC_NO_RESULTS_FOUND: "error_generic_no_results_found",

  // Trickplay Action Bar related Locale Keys
  TRICKPLAY_ACTION_BAR_RECORD: "trickplay_action_bar_record",
  TRICKPLAY_ACTION_BAR_GO_LIVE: "trickplay_action_bar_go_live",
  TRICKPLAY_ACTION_BAR_GO_LIVE_BROWSER: "trickplay_action_bar_go_live_browser",
  TRICKPLAY_ACTION_BAR_DETAILS: "trickplay_action_bar_details",
  TRICKPLAY_ACTION_BAR_AUDIO_SUBTITLES: "trickplay_action_bar_audio_subtitles",
  TRICKPLAY_ACTION_BAR_STREAM_STATS: "trickplay_action_bar_stream_stats",

  // Media Track List related Locale Keys
  MEDIA_TRACK_OPTIONS_SUBTITLES_CAPTIONS_SECTION_TITLE:
    "media_track_options_subtitles_captions_section_title",

  // Screen reader (VoiceView) spoken tile preview
  TALKBACK_SEASON_EPISODE: "talkback_season_episode",
  TALKBACK_RATING: "talkback_rating",
  TALKBACK_CHANNEL_NAME: "talkback_channel_name",
  TALKBACK_CHANNEL_NUMBER: "talkback_channel_number",
  TALKBACK_START_TIME: "talkback_start_time",
  TALKBACK_END_TIME: "talkback_end_time"
} as const;
